import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, Type } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';

import { TransactionLimitsEditFormComponent } from '@shared/components';
import { TransactionLimitItem } from '@shared/models';
import { AccessControlPipe } from '@shared/pipes';
import { TransactionLimitsService } from '@shared/services/transaction/transaction-limits.service';
import { ErrorUtils } from '@shared/utils';

import { TransactionLimitItemComponent } from '../transaction-limit-item/transaction-limit-item.component';

@Component({
  selector: 'app-transaction-limits',
  templateUrl: './transaction-limits.component.html',
  styleUrls: ['./transaction-limits.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TransactionLimitItemComponent, AsyncPipe, AccessControlPipe, RktIconComponent],
})
export class TransactionLimitsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isEditAllowed = true;

  @Input() permissionDenied = false;

  @Input() scopeId!: string | undefined;

  @Input() scopeType!: 'FA_KFA' | 'CUSTOMER' | 'USER';

  @Input() customEditModal?: {
    component: Type<unknown>;
    modalInitData: (instance: unknown, scopeId: string, limits: TransactionLimitItem[]) => void;
    onClose?: () => void;
  };

  @Input() limits?: TransactionLimitItem[];

  public transactionLimits: TransactionLimitItem[] = [];

  public hasLimits = false;

  private destroy$ = new Subject<void>();

  constructor(
    private modalService: ModalService,
    public limitsService: TransactionLimitsService,
  ) {}

  ngOnInit(): void {
    this.getTransactionLimits();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['limits'] && !changes['limits'].firstChange) {
      this.getTransactionLimits();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTransactionLimits() {
    // If limits are provided as input, use them directly
    if (this.limits) {
      this.transactionLimits = this.limits;
      return;
    }

    // Otherwise fetch from API
    if (!this.scopeId) {
      return;
    }

    this.limitsService
      .getTransactionLimits(this.scopeId, this.scopeType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (limits) => {
          this.transactionLimits = limits || [];
        },
        error: (error) => {
          ErrorUtils.catchError('limitsService.getTransactionLimits', error);
        },
      });
  }

  onEditClick(): void {
    if (this.customEditModal) {
      // Use custom modal
      const editModalRef = this.modalService.open(this.customEditModal.component, {
        className: 'entity-form-modal',
      });

      this.customEditModal.modalInitData(editModalRef.componentInstance, this.scopeId!, this.transactionLimits);

      editModalRef.result.finally(() => {
        // Call custom onClose callback if provided, otherwise default to getTransactionLimits
        if (this.customEditModal?.onClose) {
          this.customEditModal.onClose();
        } else {
          this.getTransactionLimits();
        }
      });
    } else {
      // Use default modal
      const editModalRef = this.modalService.open(TransactionLimitsEditFormComponent, {
        className: 'entity-form-modal transaction-limits-edit-modal',
      });

      editModalRef.componentInstance.modalInitData({
        financialAccountId: this.scopeId,
        transactionLimits: this.transactionLimits,
      });

      editModalRef.result.finally(() => this.getTransactionLimits());
    }
  }
}
